import { RouteObject } from 'react-router';

import { LayoutHandleProps } from 'layouts/Layout/LayoutHandleProps';
import { listingApiFetchJob } from 'modules/listing/api/job/listingApiFetchJob';
import { isListingExpired } from 'modules/listing/helpers/isListingExpired';
import { ExpiredJobPageLoadable } from 'modules/listing/pages/jobs/expired/ExpiredJobPageLoadable';
import { UnpublishedJobPageLoadable } from 'modules/listing/pages/jobs/unpublished/UnpublishedJobPageLoadable';
import { ViewJobPageLoadable } from 'modules/listing/pages/jobs/view/ViewJobPageLoadable';
import { SearchJobsRefinements } from 'modules/search/pages/Jobs/components/SearchJobsRefinements';
import { NotFoundPageLoadable } from 'pages/NotFound/NotFoundPageLoadable';
import { AppRouteLoaderArgs } from 'routing/classes/types/AppRouteLoaderArgs';
import { renderLoader } from 'routing/utils/renderLoader/renderLoader';
import { getUserEnvironment } from 'zustand-stores/userEnvironmentStore';

import { jobSegmentedRoutes } from './job.routes';

/**
 * Job routes
 *
 * These routes are separate in their own file to facilitate maintenance, and
 * they're used on `main.route-objects.tsx`
 */
export const jobRouteObjects = jobSegmentedRoutes.map((segmentedRoute) => ({
  path: segmentedRoute.fullPath,
  ...renderLoader({
    loader: async ({ params }: AppRouteLoaderArgs<typeof segmentedRoute>) => {
      const { jobIdDashSlug = '' } = params;

      const { user } = getUserEnvironment();

      try {
        const [job] = await Promise.all([
          listingApiFetchJob(jobIdDashSlug.split('-')[0]),
          // It's possible this loadable won't be used if the job is unpublished
          // but in most cases optimistically loading it is best
          ViewJobPageLoadable.load(),
        ]);

        switch (job.type) {
          case 'JOB':
            return {
              type: isListingExpired(job.expires) ? 'EXPIRED_JOB' : job.type,
              job,
              handle: {
                hideFooterDivider: Boolean(!user),
                urlByLocale: job.url,
              } satisfies LayoutHandleProps,
            };
          case 'UNPUBLISHED_JOB':
            await UnpublishedJobPageLoadable.load();
            return { type: 'UNPUBLISHED_JOB', unpublishedJob: job };
        }
      } catch (e) {
        return null;
      }
    },
    render: (data) => {
      switch (data?.type) {
        case undefined:
          return <NotFoundPageLoadable noLayout />;
        case 'JOB':
          return <ViewJobPageLoadable job={data.job} />;
        case 'EXPIRED_JOB':
          return <ExpiredJobPageLoadable job={data.job} />;
        case 'UNPUBLISHED_JOB':
          return (
            <UnpublishedJobPageLoadable unpublishedJob={data.unpublishedJob} />
          );
      }
    },
  }),
  handle: {
    defaultSearchType: 'JOB',
    searchRefinements: <SearchJobsRefinements />,
    showDesktopSearch: true,
  } satisfies LayoutHandleProps,
})) satisfies RouteObject[];
